import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReconciliationInvoicesTransactionAsync from './ReconciliationInvoicesTransactionAsync';
import useTransaction from 'Hooks/useTransaction';
import { cn } from 'Utils/cn';
import { useTranslation } from 'react-i18next';
import { getTransactionReconciliationAmount } from 'Utils/reconciliation';

function TransactionItem({ transactionId, onDelete, onAmountChange, className }) {
    const { transaction } = useTransaction(transactionId);

    useEffect(() => {
        if (transaction?.amount_uc != null) {
            onAmountChange(transactionId, getTransactionReconciliationAmount(transaction));
        }
        return () => {
            onAmountChange(transactionId, null);
        };
    }, [transaction?.amount_uc, transactionId, onAmountChange]);

    return (
        <div className={cn('border border-dark-blue/10 rounded-md px-4 py-3', className)}>
            <ReconciliationInvoicesTransactionAsync transactionId={transactionId} onDelete={onDelete} />
        </div>
    );
}

TransactionItem.propTypes = {
    transactionId: PropTypes.string.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAmountChange: PropTypes.func.isRequired,
    className: PropTypes.string,
};

export default function ReconciliationSelectedTransactions({ transactionsIds, onDeleteTransaction, onChangeTotal }) {
    const [amounts, setAmounts] = useState({});
    const { t } = useTranslation('reconciliation');

    const handleAmountChange = useCallback((transactionId, amount) => {
        setAmounts((prev) => {
            if (amount === null) {
                const { [transactionId]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [transactionId]: amount };
        });
    }, []);

    useEffect(() => {
        const total = Object.values(amounts).reduce((sum, amount) => sum + (amount || 0), 0);
        onChangeTotal(total);
    }, [amounts, onChangeTotal]);

    if (transactionsIds.length === 0) {
        return (
            <div className="relative h-[62px] flex justify-center items-center text-center rounded-md border-dashed border border-gray-300 bg-gray-50 text-xs text-gray-500">
                {t('click-transaction-to-add')}
            </div>
        );
    }

    return (
        <div className="grid gap-1">
            {transactionsIds.map((transactionId) => (
                <TransactionItem
                    key={transactionId}
                    transactionId={transactionId}
                    onDelete={() => onDeleteTransaction(transactionId)}
                    onAmountChange={handleAmountChange}
                />
            ))}
        </div>
    );
}

ReconciliationSelectedTransactions.propTypes = {
    transactionsIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    onDeleteTransaction: PropTypes.func.isRequired,
    onChangeTotal: PropTypes.func.isRequired,
};
